import { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, collection, addDoc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { useAuth } from '../AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import StripePaymentForm from '../components/StripePaymentForm';

export default function Signup() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    company: '',
    agreeToTerms: false
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  const navigate = useNavigate();
  const { signInWithGoogle } = useAuth();

  // Initialize Stripe
  useEffect(() => {
    const initStripe = async () => {
      const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
      setStripePromise(stripe);
    };
    initStripe();
  }, []);

  // Removed Google Identity Services - using Firebase auth only

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (!formData.agreeToTerms) {
      setError('Please agree to the terms and conditions');
      return;
    }

    // Show payment form instead of creating account immediately
    setShowPaymentForm(true);
  };

  const handlePaymentSuccess = async (paymentMethod) => {
    setLoading(true);
    setError(null);

    try {
      // Create user with email and password
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // Create user profile in Firestore
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        name: fullName,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        company: formData.company,
        role: 'CLIENT', // Use uppercase to match RBAC constants
        status: 'active',
        stripeCustomerId: paymentMethod.customer, // Will be set by Stripe
        createdAt: new Date(),
        updatedAt: new Date()
      });

      // Create account for the client
      const accountData = {
        name: formData.company || fullName,
        description: `Account for ${fullName}`,
        contactEmail: formData.email,
        status: 'active',
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      const accountRef = await addDoc(collection(db, 'accounts'), accountData);
      
      // Update user with account information
      await updateDoc(doc(db, 'users', userCredential.user.uid), {
        accountId: accountRef.id,
        accountName: accountData.name,
        updatedAt: new Date()
      });

      // Create Stripe subscription
      await createStripeSubscription(userCredential.user.uid, paymentMethod.id);

      // Navigate to dashboard
      navigate('/dashboard?payment_success=true');
    } catch (err) {
      const code = err.code;
      let message = 'Something went wrong. Please try again.';
      
      if (code === 'auth/email-already-in-use') {
        message = 'An account with this email already exists.';
      } else if (code === 'auth/invalid-email') {
        message = 'Please enter a valid email address.';
      } else if (code === 'auth/weak-password') {
        message = 'Password is too weak. Please choose a stronger password.';
      }

      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentError = (errorMessage) => {
    setError(errorMessage);
  };

  const createStripeSubscription = async (userId, paymentMethodId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_WORKER_URL}/api/stripe/create-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          userEmail: formData.email,
          userName: `${formData.firstName} ${formData.lastName}`,
          paymentMethodId,
          priceId: process.env.REACT_APP_STRIPE_PRICE_ID
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create subscription');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <svg width="129" height="47" viewBox="0 0 129 47" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M98.1553 37.7881C98.5299 37.7881 98.8673 37.825 99.167 37.8809C99.4668 37.9368 99.7484 38.0306 100.011 38.1611C100.273 38.273 100.516 38.4221 100.741 38.5898C100.966 38.7576 101.191 38.9248 101.397 39.1299L100.947 39.5771C100.76 39.4095 100.573 39.2418 100.386 39.0928C100.18 38.9436 99.9729 38.813 99.748 38.7012C99.5233 38.5894 99.2798 38.515 99.0176 38.4404C98.7552 38.3845 98.4551 38.3477 98.1553 38.3477C97.6494 38.3477 97.1995 38.4406 96.7686 38.627C96.3376 38.8134 95.9629 39.0746 95.6631 39.4102C95.3447 39.7456 95.1012 40.1368 94.9326 40.584C94.7453 41.0313 94.6699 41.5162 94.6699 42.0381C94.6699 42.5786 94.7639 43.0633 94.9512 43.5293C95.1385 43.9766 95.3818 44.3872 95.7002 44.7227C96.0188 45.0582 96.3944 45.3193 96.8066 45.5244C97.2376 45.7108 97.7063 45.8232 98.1934 45.8232C98.7927 45.8232 99.3171 45.7108 99.7666 45.4873C100.216 45.2636 100.647 44.9462 101.06 44.5361L101.473 44.9463C101.248 45.1513 101.023 45.3567 100.779 45.5244C100.536 45.7108 100.292 45.8598 100.029 45.9717C99.7669 46.1022 99.4668 46.1959 99.167 46.2705C98.8485 46.345 98.5112 46.3818 98.1553 46.3818L97.9141 46.377C97.4015 46.3525 96.9299 46.2426 96.4873 46.0469C95.9814 45.8232 95.5318 45.5243 95.1758 45.1328C94.8011 44.7414 94.5196 44.2943 94.3135 43.7725C94.1074 43.2506 93.9952 42.6911 93.9951 42.0947C93.9951 41.4795 94.1073 40.938 94.3135 40.416C94.5196 39.8942 94.8198 39.4471 95.1758 39.0557C95.5505 38.6642 95.9814 38.3654 96.4873 38.123C96.9932 37.8994 97.5557 37.7881 98.1553 37.7881ZM116.876 37.9561C117.401 37.9561 117.851 38.0306 118.244 38.1797C118.637 38.3288 118.974 38.5341 119.218 38.7764C119.424 38.9814 119.574 39.2048 119.687 39.4658C119.799 39.7268 119.855 40.0064 119.855 40.3047C119.855 40.6775 119.799 40.9949 119.668 41.2559C119.537 41.5353 119.368 41.7589 119.162 41.9639C118.937 42.1689 118.693 42.3177 118.394 42.4482C118.094 42.5787 117.775 42.654 117.419 42.71L120.174 46.252H119.387L116.726 42.8213H113.99V46.252H113.372V37.9561H116.876ZM43.2871 41.7959H48.6094V37.9561H49.2275V46.2324H48.6094V42.3555H43.2871V46.2324H42.6689V37.9561H43.2871V41.7959ZM58.1289 38.5146H52.8066V41.7959H57.585V42.3369H52.8066V45.6924H58.1846V46.2324H52.1885V37.9561H58.1289V38.5146ZM68.416 46.2324H67.7422L66.6738 43.9023H61.7637L60.6963 46.2324H60.0586L63.9375 37.8809H64.5371L68.416 46.2324ZM71.1338 45.6924H76.0244V46.2324H70.5156V37.9561H71.1338V45.6924ZM82.752 38.5146H79.8477V46.2324H79.2295V38.5146H76.3242V37.9561H82.752V38.5146ZM85.5068 41.7959H90.8477V37.9561H91.4658V46.2324H90.8477V42.3555H85.5068V46.2324H84.8887V37.9561H85.5068V41.7959ZM111.291 46.2324H110.617L109.549 43.9023H104.639L103.571 46.2324H102.934L106.812 37.8809H107.412L111.291 46.2324ZM128.325 38.5146H123.003V41.7959H127.782V42.3369H123.003V45.6924H128.382V46.2324H122.385V37.9561H128.325V38.5146ZM62.0078 43.3623H66.4111L64.2188 38.5713L62.0078 43.3623ZM104.883 43.3623H109.286L107.094 38.5713L104.883 43.3623ZM114.009 42.2432H116.801C117.157 42.2432 117.476 42.2065 117.775 42.1133C118.075 42.0201 118.338 41.908 118.544 41.7402C118.75 41.5725 118.919 41.3858 119.05 41.1436C119.181 40.9012 119.237 40.64 119.237 40.3418C119.237 39.764 119.03 39.317 118.618 38.9814C118.206 38.6646 117.607 38.4961 116.857 38.4961H114.009V42.2432ZM53.4629 2.27441C55.393 2.27443 57.0606 2.53565 58.4473 3.05762C59.8339 3.5796 60.9959 4.2695 61.9141 5.1084C62.8321 5.94723 63.5259 6.89755 63.9756 7.94141C64.4253 8.98529 64.7063 10.0294 64.7812 11.0732H59.291C58.9536 8.40758 57.0044 7.06547 53.4629 7.06543C51.9263 7.06543 50.6707 7.32657 49.6963 7.86719C48.7219 8.40777 48.2344 9.19052 48.2344 10.2344C48.2344 10.8122 48.3657 11.2971 48.6279 11.6699C48.8903 12.0614 49.3031 12.3787 49.8652 12.6396C50.4086 12.9006 51.1023 13.1426 51.9453 13.3477C52.7698 13.5527 53.7819 13.7764 54.9248 13.9814C56.1615 14.2238 57.4358 14.5228 58.71 14.877C60.0028 15.2125 61.1462 15.7157 62.1768 16.3682C63.2072 17.0206 64.0501 17.8595 64.7246 18.9219C65.3805 19.9845 65.7178 21.3456 65.7178 23.0234C65.7177 25.6892 64.7437 27.7772 62.7949 29.2686C60.8461 30.7599 58.1289 31.5244 54.6436 31.5244L54.0459 31.5146C52.5747 31.4696 51.1769 31.2657 49.8652 30.8906C48.3661 30.4619 47.0349 29.8466 45.873 29.0264C44.73 28.2061 43.7931 27.2176 43.0811 26.0059C42.369 24.8128 41.9572 23.4329 41.8447 21.9043H47.3721C47.5407 23.3955 48.1969 24.5702 49.3584 25.4277C50.5015 26.2853 52.1509 26.7334 54.2871 26.7334C55.1678 26.7334 55.9741 26.6774 56.7236 26.5469C57.4543 26.4164 58.0914 26.2112 58.6348 25.9316C59.1782 25.652 59.5906 25.2785 59.9092 24.8311C60.2275 24.3837 60.3779 23.8428 60.3779 23.1719C60.3778 22.501 60.1901 21.9228 59.8154 21.4941C59.4407 21.0654 58.9157 20.7114 58.2412 20.3945C57.5667 20.0963 56.7235 19.8165 55.7305 19.5928C54.7373 19.3504 53.6311 19.1272 52.4131 18.8662C51.2701 18.6239 50.1459 18.3257 49.0029 18.0088C47.8599 17.6732 46.8479 17.2069 45.9297 16.5918C45.0115 15.9766 44.2809 15.1745 43.7188 14.2051C43.1566 13.2357 42.875 11.9865 42.875 10.4766C42.8751 9.30232 43.1193 8.20289 43.6064 7.17773C44.0937 6.15241 44.8054 5.29434 45.7236 4.58594C46.6606 3.87757 47.7664 3.29976 49.0781 2.88965C50.3898 2.47958 51.8514 2.27441 53.4629 2.27441ZM74.9941 13.3477C75.5001 12.2106 76.3055 11.3155 77.4297 10.6631C78.5353 9.99196 79.7727 9.67578 81.1406 9.67578C83.5766 9.67585 85.5069 10.5146 86.8936 12.1924C88.28 13.8701 88.9736 16.2188 88.9736 19.2012V31.5244H83.6143V19.2012C83.6142 17.8779 83.2951 16.7784 82.6582 15.9023C82.0211 15.0262 80.9532 14.5968 79.4355 14.5967C77.9177 14.5967 76.8863 15.0253 76.1367 15.8828C75.3872 16.7403 75.0128 17.8591 75.0127 19.2012V31.5244H69.6533V0H74.9941V13.3477ZM98.7549 31.5244H93.3955V9.65625H98.7549V31.5244ZM122.385 2.27441C123.265 2.27441 123.959 2.98251 123.959 3.83984V9.69434H128.737V13.8701H123.959V22.9678C123.959 24.3843 124.241 25.1484 124.803 25.8008C125.365 26.4532 126.227 26.7705 127.407 26.7705C127.67 26.7705 127.932 26.7707 128.194 26.7334C128.457 26.7148 128.719 26.6585 128.981 26.6025V31.2627C128.663 31.3186 128.325 31.3747 127.932 31.4307C127.557 31.4866 127.164 31.5244 126.808 31.5244L126.335 31.5156C123.913 31.4304 122.035 30.7049 120.718 29.3242C119.312 27.8516 118.6 25.913 118.6 22.9678V13.8887H115.752V9.71289H118.6V2.27441H122.385ZM111.779 0C112.21 2.65136e-05 112.622 0.0368837 113.034 0.0927734C113.446 0.148676 113.859 0.223314 114.252 0.297852V4.79102C114.027 4.73515 113.784 4.69742 113.521 4.66016C113.259 4.62291 113.016 4.62305 112.791 4.62305C111.704 4.62305 110.805 4.94054 110.093 5.55566C109.362 6.17086 109.006 7.23323 109.006 8.72461V9.69434H113.54V13.8701H109.006V31.5059H103.646V13.8887H100.985V9.71289H103.627V8.78027C103.627 7.34492 103.834 6.07728 104.246 4.99609C104.658 3.91491 105.22 3.00151 105.951 2.25586C106.682 1.52882 107.525 0.969608 108.537 0.578125C109.53 0.205292 110.617 0 111.779 0ZM98.8857 7.5498H93.2646V2.25586H98.8857V7.5498Z" fill="#000C34"/>
            <path d="M32.7934 9.26521V23.5266C32.7934 29.7717 27.6964 34.8424 21.4188 34.8424H21.3064V17.2255C21.3064 15.268 19.7135 13.6834 17.7459 13.6834H0C0 7.38234 5.13452 2.27435 11.4683 2.27435H25.7663C29.6453 2.27435 32.7934 5.40626 32.7934 9.26521Z" fill="#3FF1C3"/>
          </svg>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Or{' '}
          <a href="/" className="font-medium text-blue-600 hover:text-blue-500">
            sign in to your existing account
          </a>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                  First Name
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                  Last Name
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
        <input
                id="email"
                name="email"
          type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-700">
                Company (Optional)
              </label>
              <input
                id="company"
                name="company"
                type="text"
                value={formData.company}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
        <input
                id="password"
                name="password"
          type="password"
                autoComplete="new-password"
                required
                value={formData.password}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
        <input
                id="confirmPassword"
                name="confirmPassword"
          type="password"
                autoComplete="new-password"
                required
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-center">
              <input
                id="agreeToTerms"
                name="agreeToTerms"
                type="checkbox"
                checked={formData.agreeToTerms}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="agreeToTerms" className="ml-2 block text-sm text-gray-900">
                I agree to the{' '}
                <button type="button" className="text-blue-600 hover:text-blue-500">
                  Terms and Conditions
                </button>{' '}
                and{' '}
                <button type="button" className="text-blue-600 hover:text-blue-500">
                  Privacy Policy
                </button>
              </label>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Creating account...' : 'Continue to Payment'}
        </button>
            </div>
      </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            <div className="mt-6">
              <button
                onClick={async () => {
                  try {
                    await signInWithGoogle();
                    navigate('/dashboard');
                  } catch (error) {
                    console.error('Google sign-in error:', error);
                    setError('Failed to sign in with Google');
                  }
                }}
                className="w-full flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mt-3"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign up with Google
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Form */}
      {showPaymentForm && stripePromise && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Complete Your Signup
                </h2>
                <button
                  onClick={() => setShowPaymentForm(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-medium text-blue-900 mb-2">Account Details</h3>
                <div className="text-sm text-blue-700 space-y-1">
                  <div><span className="font-medium">Name:</span> {formData.firstName} {formData.lastName}</div>
                  <div><span className="font-medium">Email:</span> {formData.email}</div>
                  <div><span className="font-medium">Plan:</span> Shift Healthcare Pro - $80/month</div>
                </div>
              </div>

              <Elements stripe={stripePromise}>
                <StripePaymentForm
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                  loading={loading}
                  disabled={loading}
                />
              </Elements>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
